import { createReducer, on, Action } from '@ngrx/store';
import { ManagersScope, ManagersState} from "./manager.model";
import {
  getLeaderboard,
  getManager,
  loadLeaderboardWidget, loadTopPositiveROI,
  removePinnedSyndicates,
  setPinnedSyndicates,
  successLeaderboard,
  successManager,
  successManagersList,
  successTopFollowed, successTopPositiveROI,
  successUpdateManager,
  updateManager,
  failUpdateManager
} from "../../NGRX/manager/manager.actions";
import {successCoreSettings} from "../configuration/configuration.actions";
import {successMicrositeSessions} from "../coreAuth/coreAuth.actions";
import * as _ from 'lodash-es';

export const initialState: ManagersState = {
  list: undefined,
  listParams: {
    per: 6,
    page: 1,
    filters: {
      followed: true
    }
  },
  manager: undefined,

  my_manager: undefined,

  leaderboardParams: {
    per: 20,
    page: 1,
    sort_by: 'roi',
    open_syndicates: false
  },
  leaderboardWidgetParams: {
    per: 4,
    page: 1,
    sort_by: 'roi',
    open_syndicates: false
  },
  leaderboard: undefined,
  top_followed: undefined,
  top_positive_roi: undefined
};

const reducer = createReducer(
  initialState,
  on(successManagersList, (state, action) => {
    const data = action.response;

    if(!data) return {
      ...state,
      list: undefined
    };

    const managers: ManagersScope.ManagersListParsed = {
      total: data.total_items,
      syndicates: _.orderBy(_.filter(data.items, (e) => e.open_syndicates_number > 0), ['open_syndicates_number'], ['desc']),
      noSyndicates: _.orderBy(_.filter(data.items, (e) => e.open_syndicates_number < 1), [user => user.username.toLowerCase()], ['asc'])
    };

    if((action.params?.page ?? 0) > 1) {
      // Pre-existing assumption: state.list is already populated on a "load more" (page > 1) fetch — not independently verified
      managers.syndicates = state.list!.syndicates.concat(managers.syndicates);
      managers.noSyndicates = state.list!.noSyndicates.concat(managers.noSyndicates);
    }

    return {
      ...state,
      list: managers
    }
  }),
  on(successMicrositeSessions, (state, action) => {
    return {
      ...state,
      my_manager: {
        ...(state.my_manager as ManagersScope.Manager),
        // response and manager_id are typed optional; not independently verified non-null here — pre-existing, unguarded call site
        id: action.response!.manager_id!
      }
    }
  }),
  on(successManager, (state, action) => {
    const loadedManager = action.response;
    const isMyManager = (state.my_manager?.id === action.response?.id);
    return {
      ...state,
      my_manager: isMyManager ?  loadedManager : state.my_manager,
      manager: !isMyManager ?  loadedManager : state.manager,
    }
  }),

  on(updateManager, (state) => ({
    ...state,
    update_error: null
  })),
  on(failUpdateManager, (state, action) => ({
    ...state,
    // Backend reports the reason as errors[0].code (e.g. FILE_TOO_BIG); fall back to the handler's code
    update_error: action.error?.errors?.[0]?.code ?? action.error?.code ?? 'DEFAULT'
  })),
  on(successUpdateManager, (state, action) => {
    return {
      ...state,
      update_error: null,
      my_manager: {
        ...(state.my_manager as ManagersScope.Manager),
        justUpdated: true
      }
    }
  }),
  on(getManager, (state, action) => {
    return {
      ...state,
      my_manager: {
        ...(state.my_manager as ManagersScope.Manager),
        justUpdated: false
      }
    }
  }),
  on(successTopFollowed, (state, action) => {
    return {
      ...state,
      top_followed: action.response
    }
  }),

  on(removePinnedSyndicates, (state, action) => {
    return {
      ...state,
      my_manager: {
        ...(state.my_manager as ManagersScope.Manager),
        pinnedSyndicates: []
      }
    }
  }),

  on(setPinnedSyndicates, (state, action) => {
    return {
      ...state,
      my_manager: {
        ...(state.my_manager as ManagersScope.Manager),
        pinnedSyndicates: action.id ? [action.id] : []
      }
    }
  }),

  // LEADERBOARD
  on(successCoreSettings, (state, action) => {
    return {
      ...state,
      leaderboardParams: {
        ...(state.leaderboardParams as ManagersScope.LeaderboardParams),
        period: state.leaderboardParams?.period || _.head(action.response.periods)
      },
      leaderboardWidgetParams: {
        ...(state.leaderboardWidgetParams as ManagersScope.LeaderboardParams),
        period: state.leaderboardParams?.period || _.head(action.response.periods)
      }
    }
  }),

  on(getLeaderboard, (state, action) => {
    if(!action.model.isWidget) {
      return {
        ...state,
        leaderboardParams: _.isEmpty(action.model) ? Object.assign({}, initialState.leaderboardParams, { period: state.leaderboardParams?.period }) : Object.assign({}, state.leaderboardParams, action.model)
      }
    } else {
      return {
        ...state,
        leaderboardWidgetParams: _.isEmpty(action.model) ? Object.assign({}, initialState.leaderboardWidgetParams, { period: state.leaderboardWidgetParams?.period }) : Object.assign({}, state.leaderboardWidgetParams, action.model)
      }
    }
  }),

  on(successLeaderboard, (state, action) => {
    // Pre-existing assumption: leaderboardParams/leaderboard are populated whenever a page > 1 leaderboard is received — not independently verified
    const nextPageLeaderboard = state.leaderboardParams!.page !== 1 ? {
      items: _.uniq(state.leaderboard!.items.concat(action.response.items)),
      total_items: action.response.total_items
    } : null;

    return {
      ...state,
      leaderboard: nextPageLeaderboard ? nextPageLeaderboard : action.response
    }
  }),

  on(loadLeaderboardWidget, (state, action) => {
    return {
      ...state,
      leaderboardWidgetParams: {
        ...(state.leaderboardWidgetParams as ManagersScope.LeaderboardParams),
        sportId: action.sportId
      }
    }
  }),

  on(loadTopPositiveROI, (state, action) => {
    const params = Object.assign({}, state.leaderboardWidgetParams, {per: 7, sportId: action.sportId});
    return {
      ...state,
      leaderboardWidgetParams: params
    }
  }),

  on(successTopPositiveROI, (state, action) => {
    return {
      ...state,
      top_positive_roi: action.response
    }
  }),
);

export function managersReducer(
  state: ManagersState | undefined,
  action: Action
): ManagersState {
  return reducer(state, action);
}
